import type { BudgetEvent, BudgetEventType, BudgetListener } from '../types'
import { createId } from '../utils'

/**
 * In-memory event bus for budget lifecycle events.
 * Framework-agnostic — UI/API layers subscribe without coupling to React.
 */
export class BudgetEventBus {
  private readonly listeners = new Map<BudgetEventType | '*', Set<BudgetListener>>()

  on(type: BudgetEventType | '*', listener: BudgetListener): () => void {
    const set = this.listeners.get(type) ?? new Set<BudgetListener>()
    set.add(listener)
    this.listeners.set(type, set)
    return () => {
      set.delete(listener)
      if (set.size === 0) this.listeners.delete(type)
    }
  }

  emit(event: BudgetEvent): void {
    const exact = this.listeners.get(event.type)
    const wildcard = this.listeners.get('*')
    exact?.forEach((listener) => listener(event))
    wildcard?.forEach((listener) => listener(event))
  }

  clear(): void {
    this.listeners.clear()
  }
}

export function createBudgetEvent<TPayload>(input: {
  type: BudgetEventType
  budgetId: string
  payload: TPayload
  commandId?: string
  nowIso?: string
  id?: string
}): BudgetEvent<TPayload> {
  return {
    id: input.id ?? createId(),
    type: input.type,
    budgetId: input.budgetId,
    commandId: input.commandId,
    timestamp: input.nowIso ?? new Date().toISOString(),
    payload: input.payload,
  }
}

export const budgetEventBus = new BudgetEventBus()
